import { forwardRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import TouchAppIcon from "@mui/icons-material/TouchApp";

import PintagTableRow from "./pintag-table-row";
import { Colors } from "../../../configs";
import { IBill, updateBillPintagAction } from "../../../redux/actions/pintagAction";
import { AssignPinTagModal } from "../../../components/common/pos/assign-pin-tag-modal/assign-pin-tag-modal";
import React from "react";
import { PINTAG_COLUMNS, COL_GAP } from "./pintag-table-columns";
import { useAppDispatch } from "../../../hooks";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { ICartSession } from "../../../typings/interfaces/pos/cart-session";

export interface IPintagTableProps {
  contentHeight?: string;
  data: IBill[];
  isLoading?: boolean;
  searchInProgress?: boolean;
  onScroll?: () => void;
  loadingNextPage?: boolean;
  hasMoreData?: boolean;
}

const PintagTable = forwardRef<HTMLDivElement, IPintagTableProps>(({
  data,
  isLoading,
  onScroll,
  loadingNextPage = false,
}, ref) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignBill, setAssignBill] = useState<IBill | null>(null);
  const [isSavingPintag, setIsSavingPintag] = useState(false);

  const handleOpenAssign = (row: IBill) => {
    setAssignBill(row);
    setAssignOpen(true);
  };
  const handleCloseAssign = () => setAssignOpen(false);

  const handleAssignSubmit = async (session: ICartSession) => {
    if (!assignBill || isSavingPintag) {
      if (!assignBill) handleCloseAssign();
      return;
    }

    setIsSavingPintag(true);
    try {
      await dispatch(
        updateBillPintagAction({
          billId: assignBill.id,
          color: session.pinTag?.color ?? null,
          tags: (session.pinTag?.tags ?? []).filter(Boolean),
        })
      ).unwrap();

      snackbar.showSnackbar("Pin tag updated successfully", "success");
      handleCloseAssign();
    } catch (err: any) {
      snackbar.showSnackbar(
        typeof err === "string" ? err : err?.message || "Failed to update pin tag",
        "error"
      );
    } finally {
      setIsSavingPintag(false);
    }
  };

  // Build the grid template from the same flex values used by rows.
  // Every cell — header and data — lives in this one grid, so columns
  // are always pixel-perfect aligned.
  const gridTemplateColumns = PINTAG_COLUMNS.map((c) => `${c.flex}fr`).join(" ");

  const headingCellSx = {
    background: Colors.SECONDARY,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0.5rem 0.4rem",
    color: "white",
    height: "2.92857rem",
    fontSize: "0.88rem",
    fontWeight: 600,
    overflow: "hidden",
    // sticky header — sticks within the single shared scroll container
    position: "sticky" as const,
    top: 0,
    zIndex: 2,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: Colors.BACKSHADE_GRAY,
        padding: "0.71rem",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      {/*
        Single scroll container.
        The CSS Grid inside it holds ALL cells (header + every data row),
        so the browser calculates one shared set of column widths —
        header and rows are guaranteed to align perfectly.
      */}
      <Box
        ref={ref}
        onScroll={onScroll}
        sx={{
          flex: 1,
          overflowX: "hidden",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns,
            columnGap: COL_GAP,
            rowGap: COL_GAP,
          }}
        >
          {/* ── Header row ── */}
          {PINTAG_COLUMNS.map((col, i) => (
            <Box
              key={col.key}
              sx={{
                ...headingCellSx,
                ...(i === 0 && { borderTopLeftRadius: "0.71rem" }),
                ...(i === PINTAG_COLUMNS.length - 1 && { borderTopRightRadius: "0.71rem" }),
              }}
            >
              {i === PINTAG_COLUMNS.length - 1 ? (
                <TouchAppIcon sx={{ color: "white", fontSize: "1.1rem" }} />
              ) : (
                col.label
              )}
            </Box>
          ))}

          {/* ── Loading state ── */}
          {isLoading && data.length === 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "20rem",
              }}
            >
              <CircularProgress size={40} sx={{ color: Colors.PRIMARY }} />
            </Box>
          )}

          {/* ── Empty state ── */}
          {!isLoading && data.length === 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "20rem",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                There are no bills to display at this time.
              </Typography>
            </Box>
          )}

          {/* ── Data rows ──
              Each PintagTableRow returns a React fragment whose children
              become direct grid children — no wrapper element, no alignment
              offset.
          */}
          {data.map((bill, index) => (
            <PintagTableRow
              key={bill.id || index}
              bill={bill}
              lastRow={index === data.length - 1}
              isSelected={false}
              onAssignPintag={handleOpenAssign}
            />
          ))}

          {/* ── Infinite-scroll spinner ── */}
          {loadingNextPage && data.length > 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                justifyContent: "center",
                padding: "1rem",
              }}
            >
              <CircularProgress size={20} sx={{ color: Colors.PRIMARY }} />
            </Box>
          )}
        </Box>
      </Box>

      <AssignPinTagModal
        isOpen={assignOpen}
        onClose={handleCloseAssign}
        title="Update Pintag"
        bill={assignBill as IBill}
        serviceTypeId={(assignBill as any)?.serviceTypeId ?? 0}
        itemsCount={assignBill?.totalItemCount ?? 0}
        sessionId={assignBill ? String(assignBill.billId) : ""}
        onSubmit={handleAssignSubmit}
      />
    </Box>
  );
});

export default React.memo(PintagTable);
